import {
  Button,
  GenericInput,
  Icon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle
} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {Box, CircularProgress, Typography} from '@mui/material'
import React, {useState, useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import SuccessIllustration from '../../../../../../../Assets/orderChangeResult/success.svg'
import {
  PinValidationError,
  useValidateConfirmationPin
} from '../../../hooks/useValidateConfirmationPin'

type ModalState = 'input' | 'loading' | 'success' | 'error'

interface PinValidationModalProps {
  isOpen: boolean
  onClose: () => void
  customerId: string
  onValidationSuccess: (pin: string) => void
}

/**
 * Modal for PIN validation with input, loading, success, and error states.
 */
export const PinValidationModal: React.FC<PinValidationModalProps> = ({
  isOpen,
  onClose,
  customerId,
  onValidationSuccess
}) => {
  const {t} = useTranslation()
  const {validatePin, isLoading, reset} = useValidateConfirmationPin()

  const [pin, setPin] = useState('')
  const [validatedPin, setValidatedPin] = useState<string | null>(null)
  const [modalState, setModalState] = useState<ModalState>('input')
  const [error, setError] = useState<PinValidationError | null>(null)

  const handleClose = useCallback(() => {
    setPin('')
    setValidatedPin(null)
    setModalState('input')
    setError(null)
    reset()
    onClose()
  }, [onClose, reset])

  // Handler for closing the success modal - triggers onValidationSuccess
  const handleCloseSuccess = useCallback(() => {
    if (validatedPin) {
      onValidationSuccess(validatedPin)
    }
    handleClose()
  }, [validatedPin, onValidationSuccess, handleClose])

  const handleSend = useCallback(async () => {
    if (!pin.trim()) return

    setModalState('loading')
    setError(null)

    const result = await validatePin({customerId, pin})

    if (result.isValid) {
      setValidatedPin(pin.trim())
      setModalState('success')
    } else {
      setModalState('error')
      setError(result.error || null)
    }
  }, [customerId, pin, validatePin])

  const handlePinChange = useCallback(
    (value: string) => {
      setPin(value)
      // Clear error when user starts typing again
      if (error) {
        setError(null)
        setModalState('input')
      }
    },
    [error]
  )

  const isInputDisabled = modalState === 'loading'
  const isSendDisabled = !pin.trim() || modalState === 'loading'
  const showError = modalState === 'error' && error?.type === 'invalid_pin'
  const showNetworkError = modalState === 'error' && error?.type === 'network_error'

  // Success state
  if (modalState === 'success') {
    return (
      <Modal open={isOpen} onClose={handleCloseSuccess} data-test-id="pin-validation-modal-success">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <ModalTitle>{t('confirmationPin.modal.successTitle')}</ModalTitle>
          <Box
            onClick={handleCloseSuccess}
            data-test-id="pin-modal-close-button"
            sx={{
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <Icon path={mdiClose} />
          </Box>
        </Box>
        <ModalContent>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#54626F',
                textAlign: 'start'
              }}
            >
              {t('confirmationPin.modal.successDescription')}
            </Typography>
            <Box sx={{padding: '24px 0', display: 'flex', justifyContent: 'center'}}>
              <img
                src={SuccessIllustration}
                alt="Success"
                style={{width: '240px', height: 'auto'}}
              />
            </Box>
          </Box>
        </ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={handleCloseSuccess}
            data-test-id="pin-modal-close-success-button"
            className="w-full"
          >
            {t('confirmationPin.modal.closeButton')}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  // Input/Loading/Error state
  return (
    <Modal open={isOpen} onClose={handleClose} data-test-id="pin-validation-modal">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <ModalTitle>{t('confirmationPin.modal.title')}</ModalTitle>
        <Box
          onClick={handleClose}
          data-test-id="pin-modal-close-button"
          sx={{
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <Icon path={mdiClose} />
        </Box>
      </Box>
      <ModalContent>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: '#54626F'
            }}
          >
            {t('confirmationPin.modal.description')}
          </Typography>

          <GenericInput
            label={t('confirmationPin.modal.pinLabel')}
            value={pin}
            onChange={handlePinChange}
            isDisabled={isInputDisabled}
            error={showError ? t('confirmationPin.modal.invalidPinError') : undefined}
            isTouched={showError}
            data-test-id="pin-input"
          />

          {showNetworkError && (
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#EF4444'
              }}
            >
              {t('confirmationPin.modal.genericError')}
            </Typography>
          )}
        </Box>
      </ModalContent>
      <ModalFooter>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '12px', width: '100%'}}>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={isSendDisabled}
            data-test-id="pin-modal-send-button"
            className="w-full"
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t('confirmationPin.modal.sendButton')
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
            data-test-id="pin-modal-cancel-button"
            className="w-full"
          >
            {t('confirmationPin.modal.cancelButton')}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  )
}
